
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as config from '../config'
import * as history from '../history'
import * as client from '../client'
import * as feed from '../feed'
import * as storage from '../storage'
import * as exposed from '../exposed'
import * as execution from '../execution'
import * as backup from '../backup'
import * as inner from '../inner'
import * as trade from '../trade'
import * as security from '../security'
import * as document from '../document'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {Request} from '../core/Request';
import {FieldModelRequest} from '../core/Request';
import {SwapsExportResponse} from './SwapsExportResponse';

/**
 * SwapsExportRequest struct
 */
class SwapsExportRequest extends Request {
  /**
   * Initialize struct
   * @param {!Request=} parent
   * @param {!boolean=} all
   * @param {!Array=} symbolNames
   * @param {!Array=} symbolGroupNames
   * @constructor
   */
  constructor (parent = new Request(), all = false, symbolNames = [], symbolGroupNames = []) {
    super()
    super.copy(parent)
    this.All = all
    this.SymbolNames = symbolNames
    this.SymbolGroupNames = symbolGroupNames
  }

  /**
   * Copy struct (shallow copy)
   * @this {!SwapsExportRequest}
   * @param {!SwapsExportRequest} other Other struct
   * @returns {!SwapsExportRequest} This struct
   */
  copy (other) {
    super.copy(other)
    if (other.All != null) {
      this.All = other.All
    } else {
      this.All = null
    }
    if (other.SymbolNames != null) {
      this.SymbolNames = []
      for (let item of other.SymbolNames) {
        if (item != null) {
          let tempItem
          tempItem = item
          this.SymbolNames.push(tempItem)
        } else {
          this.SymbolNames.push(null)
        }
      }
    } else {
      this.SymbolNames = null
    }
    if (other.SymbolGroupNames != null) {
      this.SymbolGroupNames = []
      for (let item of other.SymbolGroupNames) {
        if (item != null) {
          let tempItem
          tempItem = item
          this.SymbolGroupNames.push(tempItem)
        } else {
          this.SymbolGroupNames.push(null)
        }
      }
    } else {
      this.SymbolGroupNames = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!SwapsExportRequest}
   * @returns {!SwapsExportRequest} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new SwapsExportRequestModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new SwapsExportRequestModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!SwapsExportRequest}
   * @param {!SwapsExportRequest} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof SwapsExportRequest)) {
      throw new TypeError('Instance of SwapsExportRequest is required!')
    }
    // noinspection RedundantIfStatementJS
    if (!super.eq(other)) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!SwapsExportRequest}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    let parent = super.toJSON()
    let current = {
      All: ((this.All != null) ? this.All : null),
      SymbolNames: ((this.SymbolNames != null) ? Array.from(this.SymbolNames, item => ((item != null) ? item : null)) : null),
      SymbolGroupNames: ((this.SymbolGroupNames != null) ? Array.from(this.SymbolGroupNames, item => ((item != null) ? item : null)) : null)
    }
    return { ...parent, ...current }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return SwapsExportRequest.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!SwapsExportRequest} other Object value
   * @returns {!SwapsExportRequest} Created struct
   */
  static fromObject (other) {
    return new SwapsExportRequest().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!SwapsExportRequest}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return SwapsExportRequest.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!SwapsExportRequest}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 6118
  }
}

export { SwapsExportRequest };

/**
 * Fast Binary Encoding SwapsExportRequest field model
 */
class FieldModelSwapsExportRequest extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this._parent = new FieldModelRequest(buffer, 4 + 4)
    this.All = new fbe.FieldModelBool(buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4)
    this.SymbolNames = new fbe.FieldModelVector(new fbe.FieldModelString(buffer, this.All.fbeOffset + this.All.fbeSize), buffer, this.All.fbeOffset + this.All.fbeSize)
    this.SymbolGroupNames = new fbe.FieldModelVector(new fbe.FieldModelString(buffer, this.SymbolNames.fbeOffset + this.SymbolNames.fbeSize), buffer, this.SymbolNames.fbeOffset + this.SymbolNames.fbeSize)
  }

  /**
   * Get the core.Request field model
   * @this {!FieldModelSwapsExportRequest}
   * @returns {!FieldModelRequest} core.Request field model
   */
  get parent () {
    return this._parent
  }

  /**
   * Get the field size
   * @this {!FieldModelSwapsExportRequest}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelSwapsExportRequest}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.parent.fbeBody - 4 - 4 + this.All.fbeSize + this.SymbolNames.fbeSize + this.SymbolGroupNames.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelSwapsExportRequest}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.parent.fbeExtra + this.All.fbeExtra + this.SymbolNames.fbeExtra + this.SymbolGroupNames.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelSwapsExportRequest}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelSwapsExportRequest.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelSwapsExportRequest}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 6118
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelSwapsExportRequest}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelSwapsExportRequest}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) > fbeStructSize) {
      return true
    }
    if (!this.parent.verifyFields(fbeStructSize)) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.All.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.All.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.All.fbeSize

    if ((fbeCurrentSize + this.SymbolNames.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.SymbolNames.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.SymbolNames.fbeSize

    if ((fbeCurrentSize + this.SymbolGroupNames.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.SymbolGroupNames.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.SymbolGroupNames.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelSwapsExportRequest}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelSwapsExportRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelSwapsExportRequest}
   * @param {!SwapsExportRequest} fbeValue Default value, defaults is new SwapsExportRequest()
   * @returns {!SwapsExportRequest} SwapsExportRequest value
   */
  get (fbeValue = new SwapsExportRequest()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelSwapsExportRequest}
   * @param {!SwapsExportRequest} fbeValue SwapsExportRequest value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) <= fbeStructSize) {
      this.parent.getFields(fbeValue, fbeStructSize)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.All.fbeSize) <= fbeStructSize) {
      fbeValue.All = this.All.get()
    } else {
      fbeValue.All = false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.All.fbeSize

    if ((fbeCurrentSize + this.SymbolNames.fbeSize) <= fbeStructSize) {
      this.SymbolNames.get(fbeValue.SymbolNames)
    } else {
      fbeValue.SymbolNames.length = 0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.SymbolNames.fbeSize

    if ((fbeCurrentSize + this.SymbolGroupNames.fbeSize) <= fbeStructSize) {
      this.SymbolGroupNames.get(fbeValue.SymbolGroupNames)
    } else {
      fbeValue.SymbolGroupNames.length = 0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.SymbolGroupNames.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelSwapsExportRequest}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelSwapsExportRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelSwapsExportRequest}
   * @param {!SwapsExportRequest} fbeValue SwapsExportRequest value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelSwapsExportRequest}
   * @param {SwapsExportRequest} fbeValue SwapsExportRequest value
   */
  setFields (fbeValue) {
    this.parent.setFields(fbeValue)
    this.All.set(fbeValue.All)
    this.SymbolNames.set(fbeValue.SymbolNames)
    this.SymbolGroupNames.set(fbeValue.SymbolGroupNames)
  }
}

export { FieldModelSwapsExportRequest };

/**
 * Fast Binary Encoding SwapsExportRequest model
 */
class SwapsExportRequestModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelSwapsExportRequest(this.buffer, 4)
  }

  /**
   * Get the SwapsExportRequest model
   * @this {!SwapsExportRequestModel}
   * @returns {!FieldModelSwapsExportRequest} model SwapsExportRequest model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!SwapsExportRequestModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!SwapsExportRequestModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return SwapsExportRequestModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!SwapsExportRequestModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelSwapsExportRequest.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!SwapsExportRequestModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!SwapsExportRequestModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!SwapsExportRequestModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!SwapsExportRequestModel}
   * @param {!SwapsExportRequest} value SwapsExportRequest value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!SwapsExportRequestModel}
   * @param {!SwapsExportRequest} value SwapsExportRequest value, defaults is new SwapsExportRequest()
   * @return {!object} Deserialized SwapsExportRequest value and its size
   */
  deserialize (value = new SwapsExportRequest()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new SwapsExportRequest(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new SwapsExportRequest(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!SwapsExportRequestModel}
   * @param {!number} prev Previous SwapsExportRequest model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { SwapsExportRequestModel };
SwapsExportRequest.__has_response = true;
SwapsExportRequest.__response_class = SwapsExportResponse;
